import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

function GoogleButton({ onClick }) {
  return (
    <Button type="button" variant="outline" onClick={onClick} data-testid="google-login-btn"
      className="w-full gap-2 rounded-xl border-white/15 bg-white/5 py-6 text-slate-100 hover:bg-white/10">
      <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5c1.6 0 3 .55 4.1 1.6L19 3.7C17.1 1.9 14.7.9 12 .9 7.7.9 4 3.4 2.2 7l3.4 2.6C6.5 6.9 9 5 12 5z"/><path fill="#4285F4" d="M23.1 12.3c0-.8-.1-1.4-.2-2H12v3.9h6.2c-.1 1-.8 2.5-2.3 3.5l3.5 2.7c2.1-1.9 3.7-4.8 3.7-8.1z"/><path fill="#FBBC05" d="M5.6 14.3c-.2-.7-.4-1.4-.4-2.3s.1-1.6.4-2.3L2.2 7C1.4 8.5 1 10.2 1 12s.4 3.5 1.2 5l3.4-2.7z"/><path fill="#34A853" d="M12 23.1c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.3 1.1-3.8 1.1-3 0-5.5-1.9-6.4-4.6L2.2 17C4 20.6 7.7 23.1 12 23.1z"/></svg>
      Continue with Google
    </Button>
  );
}

export function AuthModal() {
  const { authOpen, setAuthOpen, login, register, googleLogin, formatApiErrorDetail } = useAuth();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") { await login(form.email, form.password); toast.success("Welcome back!"); }
      else { await register(form.email, form.password, form.name); toast.success("Account created!"); }
      setAuthOpen(false);
      setForm({ email: "", password: "", name: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="border-white/10 bg-[#111420] text-slate-100 sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            <span className="text-[#00F0FF]">Drop</span> Worldwide Account
          </DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid w-full grid-cols-2 bg-white/5">
            <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Create Account</TabsTrigger>
          </TabsList>
          <form onSubmit={submit} className="mt-5 space-y-4">
            {tab === "register" && (
              <div>
                <Label className="text-slate-300">Full Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                  className="mt-1.5 border-white/10 bg-white/5" data-testid="auth-name-input" placeholder="Jane Doe" />
              </div>
            )}
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
                className="mt-1.5 border-white/10 bg-white/5" data-testid="auth-email-input" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6}
                className="mt-1.5 border-white/10 bg-white/5" data-testid="auth-password-input" placeholder="••••••••" />
            </div>
            {tab === "login" && (
              <button type="button" onClick={() => { setAuthOpen(false); navigate("/forgot-password"); }} className="text-xs text-[#00F0FF] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </button>
            )}
            <Button type="submit" disabled={loading} className="w-full rounded-xl bg-[#00F0FF] py-6 font-bold text-[#08090E] hover:bg-[#33F3FF]" data-testid="auth-submit-btn">
              {loading ? "Please wait…" : tab === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>
          <div className="my-4 flex items-center gap-3 text-xs text-slate-500">
            <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
          </div>
          <GoogleButton onClick={googleLogin} />
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
